import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { FiUsers, FiClock, FiUserCheck, FiCheckCircle, FiTrendingUp } from "react-icons/fi";
import { useCycle } from "../../context/CycleContext";
import { getHrDashboard } from "../../services/dashboardApi";
import useOrgFilters from "../../hooks/useOrgFilters"; // [NEW] shared organisation filters
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, StatTile, Loader, ErrorState, ProgressBar, DataTable, PersonCell, StatusBadge, ChartFrame, OrgFilters } from "../../components/common";
import { axisProps, tooltipProps, TONE_COLORS, CHART_SERIES } from "../../utils/chartTheme";
import { formatScore } from "../../utils/format";
import { formatRatingScore } from "../../utils/ratingScale"; // [NEW] PM-13: score on the chosen scale

const api = { getHrDashboard };

export function CompletionBars({ completion }) {
  const rows = [
    { label: "Goal sheets approved", value: completion.goalsApproved },
    { label: "Self assessments submitted", value: completion.selfSubmitted },
    { label: "Manager assessments submitted", value: completion.managerSubmitted },
    { label: "Appraisals finalized", value: completion.finalized },
  ].filter((r) => r.value !== null && r.value !== undefined);
  return (
    <ol className="funnel">
      {rows.map((r, i) => (
        <li key={r.label}>
          <span className="funnel-step">{i + 1}</span>
          <div className="funnel-body">
            <div className="funnel-head"><span>{r.label}</span><strong>{r.value}%</strong></div>
            <ProgressBar value={r.value} tone={r.value === 100 ? "success" : "primary"} label={r.label} />
          </div>
        </li>
      ))}
    </ol>
  );
}

export function RatingDistributionChart({ data, height = 240 }) {
  return (
    <ChartFrame label="Rating distribution" summary={data.map((d) => ({ label: d.rating, value: `${d.count} employees` }))}>
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="#EEF0F6" />
        <XAxis dataKey="rating" {...axisProps} interval={0} tickFormatter={(v) => v.split(" ")[0]} />
        <YAxis allowDecimals={false} {...axisProps} />
        <Tooltip {...tooltipProps} formatter={(v) => [v, "Employees"]} />
        <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={48}>
          {data.map((d) => <Cell key={d.rating} fill={TONE_COLORS[d.tone] || TONE_COLORS.neutral} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
    </ChartFrame>
  );
}

export function DepartmentScoreChart({ data, height = 240 }) {
  const rows = data.map((d) => ({ name: d.name, score: d.avgScore ?? 0, headcount: d.headcount }));
  return (
    <ChartFrame label="Average score by department" summary={data.map((d) => ({ label: d.name, value: d.avgScore === null ? "Not rated" : d.avgScore }))}>
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 16, left: 24, bottom: 0 }}>
        <CartesianGrid horizontal={false} stroke="#EEF0F6" />
        <XAxis type="number" domain={[0, 100]} {...axisProps} />
        <YAxis type="category" dataKey="name" width={120} {...axisProps} />
        <Tooltip {...tooltipProps} formatter={(v) => [v || "Not rated", "Average score"]} />
        <Bar dataKey="score" fill="#3AA0E8" radius={[0, 6, 6, 0]} maxBarSize={22} />
      </BarChart>
    </ResponsiveContainer>
    </ChartFrame>
  );
}

export default function HrDashboard() {
  const { cycleId, cycle } = useCycle();
  const navigate = useNavigate();
  const org = useOrgFilters(); // [NEW] Business Unit, Department, Location, Cost Centre, Legal Entity
  const { data, loading, error, reload } = useAsync(() => api.getHrDashboard(cycleId, { employeeFilter: org.matches }), [cycleId, org.matches], { skip: !cycleId || org.loading });

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <Loader />;

  const pending = data.rows.filter((r) => r.resultStatus === "Pending HR Review");
  const goalStatus = data.goalStatus.filter((g) => g.count > 0);

  return (
    <div className={`page ${loading ? "is-refreshing" : ""}`}>
      <PageHeader
        title="Organization performance"
        subtitle={`Progress across ${data.companyName || "the organisation"} for ${cycle?.name}.`}
      />
      <div className="filter-bar"><OrgFilters org={org} /></div>

      <div className="stat-grid">
        <StatTile icon={FiUsers} label="Active employees" value={data.headcount} onClick={() => navigate("/hr/employees")} />
        <StatTile icon={FiClock} tone="warning" label="Goal sheets with managers" value={data.counts.pendingApproval} hint={`${data.counts.notStarted} not started`} />
        <StatTile icon={FiUserCheck} tone="danger" label="Waiting for HR" value={data.counts.pendingHr} hint="Ready to finalize" onClick={() => navigate("/hr/review")} />
        <StatTile icon={FiCheckCircle} tone="success" label="Finalized" value={data.counts.finalized} />
        <StatTile icon={FiTrendingUp} tone="info" label="Average score" value={formatScore(data.avgScore)} hint={`Official scores only (${data.scoreBasis})`} />
      </div>

      <div className="grid-2">
        <Card title="Cycle completion" subtitle="Share of active employees at each step">
          <CompletionBars completion={data.completion} />
        </Card>
        <Card title="Goal sheet status">
          <ChartFrame label="Goal sheet status" summary={goalStatus.map((g) => ({ label: g.status, value: g.count }))}>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={goalStatus} dataKey="count" nameKey="status" innerRadius={58} outerRadius={90} paddingAngle={2}>
                  {goalStatus.map((g, i) => <Cell key={g.status} fill={CHART_SERIES[i % CHART_SERIES.length]} />)}
                </Pie>
                <Tooltip {...tooltipProps} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Card>
      </div>

      <div className="grid-2">
        <Card title="Rating distribution" subtitle="Includes appraisals pending HR review">
          <div className="chart-box"><RatingDistributionChart data={data.ratingDistribution} /></div>
        </Card>
        <Card title="Average score by department">
          <div className="chart-box"><DepartmentScoreChart data={data.departments} /></div>
        </Card>
      </div>

      <div className="grid-2">
        <Card title="Employees by department">
          <ChartFrame label="Employees by department" summary={data.departments.map((d) => ({ label: d.name, value: `${d.headcount} employees` }))}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.departments} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#EEF0F6" />
                <XAxis dataKey="name" {...axisProps} interval={0} tickFormatter={(v) => v.split(" ")[0]} />
                <YAxis allowDecimals={false} {...axisProps} />
                <Tooltip {...tooltipProps} formatter={(v) => [v, "Employees"]} />
                <Bar dataKey="headcount" fill="#5B4FE0" radius={[6, 6, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Card>
        <Card title="Ready for finalization" padded={false}>
          <DataTable
            dense
            pageSize={5}
            rowKey={(r) => r.employee.id}
            rows={pending}
            emptyTitle="Nothing waiting"
            emptyText="Appraisals appear here after the manager submits a review."
            onRowClick={(r) => navigate(`/hr/review/${r.employee.id}`)}
            columns={[
              { key: "name", label: "Employee", render: (r) => <PersonCell name={r.employee.name} meta={r.employee.departmentName} size={30} /> },
              { key: "score", label: "Score", align: "right", render: (r) => formatRatingScore(r.performance.finalScore) },
              { key: "status", label: "Status", render: (r) => <StatusBadge status={r.resultStatus} /> },
            ]}
          />
        </Card>
      </div>

      {(data.unmappedEmployees.length > 0 || data.exceptions.length > 0) && (
        <div className="grid-2">
          <Card title="Not in this cycle" subtitle="Active employees without a performance record" padded={false}
            actions={<button type="button" className="link-btn" onClick={() => navigate("/hr/cycles")}>Add to cycle</button>}>
            <DataTable
              dense
              pageSize={5}
              rowKey="id"
              rows={data.unmappedEmployees}
              emptyTitle="Everyone is mapped"
              columns={[{ key: "name", label: "Employee", render: (r) => <PersonCell name={r.name} meta={r.id} size={30} /> }]}
            />
          </Card>
          <Card title="Open Issues" subtitle="Open issues must be resolved before finalization." padded={false}>
            <DataTable keepOrder
              dense
              pageSize={5}
              rowKey="exception_id"
              rows={data.exceptions}
              emptyTitle="No open exceptions"
              columns={[
                { key: "exception_code", label: "Code" },
                { key: "severity", label: "Severity", render: (r) => <StatusBadge status={r.severity} tone={r.severity === "BLOCKING" ? "danger" : "warning"} /> },
                { key: "description", label: "Description" },
              ]}
            />
          </Card>
        </div>
      )}
    </div>
  );
}
